import type { FC, PropsWithChildren } from "react"
import { useEffect, useMemo, useState, useRef } from "react"

import type { Map as LeafletMap, Marker as LeafletMarker } from "leaflet"
import northCrs from "leaflet-nearmap/crs/north"
import northLayer from "leaflet-nearmap/layers/north"
import { MapContainer, Marker } from "react-leaflet"
import { useRecoilState, useRecoilValue } from "recoil"
import styled from "styled-components"

import { positionState } from "~/containers/Proposal/state/site/site-state"
import EnvVars from "~/env-vars"
import type { Setter } from "~/hooks/types"
import useLoadGoogleMaps from "~/hooks/useLoadGoogleMaps"

const ZOOM = 20

const SizeLimiter = styled.div`
  height: calc(100vh - 400px);
  min-height: 400px;
  width: 100%;
  position: relative;
  * {
    font-size: 11px;
  }
  > * {
    height: 100%;
    width: 100%;
  }
`

const DraggableMarker: FC = () => {
  const markerRef = useRef<LeafletMarker | null>(null)
  const [position, setPosition] = useRecoilState(positionState)

  const eventHandlers = useMemo(
    () => ({
      dragend() {
        const marker = markerRef.current
        if (marker) {
          const { lat, lng } = marker.getLatLng()
          setPosition([lat, lng])
        }
      },
    }),
    [setPosition],
  )

  useEffect(() => {
    const marker = markerRef.current
    if (marker) marker.setLatLng(position)
  }, [position])

  return (
    <Marker
      draggable
      eventHandlers={eventHandlers}
      position={position}
      ref={markerRef}
    />
  )
}

type MapComponentProps = PropsWithChildren<{
  setMap: Setter<LeafletMap | null>
}>

const MapComponent: FC<MapComponentProps> = ({ children, setMap }) => {
  const [position] = useRecoilState(positionState)
  const ApiKey = EnvVars.NEARMAP_API_KEY
  const UrlTemplate = `https://api.nearmap.com/tiles/v3/Vert/{z}/{x}/{y}.img?tertiary=satellite&apikey=${ApiKey}`

  if (position[0] === 0 && position[1] === 0) return null

  return (
    <MapContainer
      center={position}
      zoom={ZOOM}
      scrollWheelZoom={true}
      ref={setMap}
      placeholder={<p>loading...</p>}
      crs={northCrs}
      layers={[northLayer(UrlTemplate)]}
    >
      {children}
    </MapContainer>
  )
}

export const GeocodeMap: FC = () => {
  const position = useRecoilValue(positionState)
  const [map, setMap] = useState<LeafletMap | null>(null)
  const googleIsLoaded = useLoadGoogleMaps()

  useEffect(() => {
    if (map) map.setView(position)
  }, [position, map])

  return (
    <SizeLimiter>
      {googleIsLoaded && (
        <MapComponent setMap={setMap}>
          <DraggableMarker />
        </MapComponent>
      )}
    </SizeLimiter>
  )
}
